
// src/App.js
import { useEffect, useState } from "react";
import { Element, Link } from "react-scroll";
import { motion } from "framer-motion";
import AboutSection from './components/AboutSection';
import TokenomicsSection from './components/TokenomicsSection';
import TrustSection from './components/TrustSection';
import LeaderboardSection from "./components/LeaderboardSection";
import RoadmapSection from "./components/RoadmapSection";
import CountdownTimer from "./components/CountdownTimer";
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import TermsModal from './components/TermsModal';
import HeroGlow from './components/HeroGlow';

function App() {
  console.log("[SHTBX] build=2025-11-12-modal");
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Modal state
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  
  // ✅ FINAL: Sentinel-based scroll check
  useEffect(() => {
    const sentinel = document.getElementById("hero-sentinel");
    if (!sentinel) return;

    const updateScrolledState = () => {
      const rect = sentinel.getBoundingClientRect();
      setScrolledPastHero(rect.top <= 0);
    };

    updateScrolledState();
    window.addEventListener("scroll", updateScrolledState);
    return () => window.removeEventListener("scroll", updateScrolledState);
  }, []);

  // Update portrait/landscape class on body
  useEffect(() => {
    const updateOrientationClass = () => {
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      document.body.classList.toggle("portrait", isPortrait);
      document.body.classList.toggle("landscape", !isPortrait);
    };

    updateOrientationClass();
    window.addEventListener("resize", updateOrientationClass);
    return () => window.removeEventListener("resize", updateOrientationClass);
  }, []);

  // Track screen size
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Detect mobile landscape (wide but short viewport)
  const isMobileLandscape = screenSize.width > screenSize.height && screenSize.height < 500;
  const isPortraitMobile = screenSize.width < 768;
  // Detect tablet landscape (medium width, not full desktop) - e.g. Galaxy Tab S4 1138x712
  const isTabletLandscape = screenSize.width >= 768 && screenSize.width < 1280 && screenSize.height < 900;

  return (
    <div className="w-full text-white bg-black">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center transition-colors duration-300 bg-black ${scrolledPastHero ? "bg-opacity-100 shadow-md backdrop-blur-sm" : "bg-opacity-0"}`}>
        {/* Logo */}
        <div className="w-[100px] md:w-[150px]">
          <Link to="hero" smooth duration={500} className="cursor-pointer transition-opacity duration-300">
            <img
              src="/images/Menu_Logo.png"
              alt="Shut The Box Logo"
              className={`h-7 md:h-16 w-auto transition-opacity duration-300 ${scrolledPastHero ? "opacity-100" : "opacity-0"}`}
            />
          </Link>
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center space-x-5 text-sm md:text-base font-rubik font-medium text-white">
          {["hero", "about", "token", "leaderboard", "roadmap"].map((section) => (
            <Link
              key={section}
              to={section}
              smooth
              duration={500}
              spy
              offset={0}
              activeClass="text-[#ff4db8]"
              className="hover:text-[#ff4db8] transition-colors duration-200 cursor-pointer"
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </Link>
          ))}
          {/* Play Now */}
          <a
            href="https://shutthebox.io/alpha_a/index.html"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-4 inline-block bg-[#ff4db8] hover:bg-[#d83a9f] text-white px-5 py-2 rounded-full text-sm md:text-base font-semibold shadow-md transition-all duration-200"
          >
            Play Now
          </a>
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden">
          <button onClick={() => setMobileMenuOpen(!isMobileMenuOpen)} className="text-white focus:outline-none">
            <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path fillRule="evenodd" d="M18.36 6.64a1 1 0 010 1.41L13.41 13l4.95 4.95a1 1 0 11-1.41 1.41L12 14.41l-4.95 4.95a1 1 0 11-1.41-1.41L10.59 13 5.64 8.05a1 1 0 011.41-1.41L12 11.59l4.95-4.95a1 1 0 011.41 0z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M4 5h16v2H4V5zm0 6h16v2H4v-2zm0 6h16v2H4v-2z" clipRule="evenodd" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black bg-opacity-100 text-white px-6 pt-20 pb-6 space-y-4 fixed top-0 left-0 right-0 z-50 shadow-lg h-screen overflow-y-auto">
          <button onClick={() => setMobileMenuOpen(false)} className="absolute top-4 right-4 text-white text-3xl focus:outline-none" aria-label="Close menu">
            &times;
          </button>

          {["hero", "about", "token", "leaderboard", "roadmap"].map((section) => (
            <Link
              key={section}
              to={section}
              smooth
              duration={500}
              spy
              offset={0}
              onClick={() => setMobileMenuOpen(false)}
              activeClass="text-[#ff4db8]"
              className="block text-lg font-medium"
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </Link>
          ))}
          {/* Play Now (mobile) */}
          <a
            href="https://shutthebox.io/alpha_a/index.html"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
            className="inline-block bg-[#ff4db8] hover:bg-[#d83a9f] text-white px-5 py-2 rounded-full text-base font-semibold shadow-md transition-all duration-200"
          >
            Play Now
          </a>
        </div>
      )}

      {/* Hero Section */}
      <Element id="hero" name="hero" className="relative min-h-screen flex items-center justify-center pt-1 px-4 text-white text-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#41003c] to-[#c62c7a]" />
          <div className="absolute inset-0 bg-[url('/public/images/bg-dice-pattern2.svg')] bg-repeat bg-[length:80px_80px] opacity-50 animate-scrollBackground" />
        </div>

        {/* Organic glow effect behind hero visuals */}
        <div className="absolute inset-0 z-[5]">
          <HeroGlow />
        </div>

        {/* Foreground */}
        <div
          className="relative z-10 w-full flex flex-col items-center justify-center min-h-screen px-4 text-white text-center"
          style={{
            paddingTop: isMobileLandscape ? '60px' : isPortraitMobile ? '40px' : '0',
            transform: isMobileLandscape ? 'none' : isPortraitMobile ? 'translateY(-100px)' : 'translateY(-100px)',
          }}
        >
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: "easeOut" }} className="relative w-full max-w-[900px] px-4 md:px-0 mb-6 md:mb-0">
            <div className="relative w-full max-w-[900px] mx-auto">
              {/* Headline */}
              <img
                src="/images/hero-headline.png"
                alt="Shut The Box"
                className="mx-auto relative"
                style={{
                  width: isMobileLandscape
                    ? '320px'
                    : screenSize.width < 480
                    ? '280px'
                    : screenSize.width < 768
                    ? '360px'
                    : isTabletLandscape
                    ? '520px'
                    : screenSize.width < 1024
                    ? '500px'
                    : '700px',
                  position: 'relative',
                  top: isMobileLandscape
                    ? '-40px'
                    : isPortraitMobile
                    ? '-100px'
                    : screenSize.height < 500
                    ? '0px'
                    : screenSize.height < 768
                    ? '-40px'
                    : screenSize.height < 1024
                    ? '40px'
                    : '-80px',
                }}
              />
            </div>

            {/* Dice */}
            <motion.img
              src="/images/hero-dice.png"
              alt="Dice"
              initial={{ opacity: 0, x: -20, rotate: -15 }}
              animate={{ opacity: 1, x: 0, rotate: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.4 }}
              className="absolute z-20 hero-dice"
              style={{
                width: isMobileLandscape
                  ? '80px'
                  : screenSize.width < 400
                  ? '100px'
                  : screenSize.width < 768
                  ? '120px'
                  : isTabletLandscape
                    ? '110px'
                    : screenSize.width < 1024
                  ? '120px'
                  : '160px',
                left: isMobileLandscape
                  ? '30%'
                  : screenSize.width < 400
                  ? 'calc(50% - 50px)'
                  : screenSize.width < 500
                  ? 'calc(50% - 60px)'
                  : screenSize.width < 768
                  ? 'calc(50% - 70px)'
                  : isTabletLandscape
                  ? '12%'
                  : screenSize.width < 1024
                  ? '200px'
                  : '40px',
                bottom: isMobileLandscape
                  ? '0px'
                  : isPortraitMobile
                  ? screenSize.height < 600
                    ? '-60px'
                    : screenSize.height < 700
                    ? '-80px'
                    : screenSize.height < 800
                    ? '-100px'
                    : '-120px'
                  : screenSize.height < 500
                  ? '-60px'
                  : screenSize.height < 768
                  ? '-120px'
                  : screenSize.height < 1024
                  ? '-80px'
                  : '60px',
              }}
            />

            <motion.img
              src="/images/hero-chest.png"
              alt="Chest"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              className="absolute z-10"
              style={{
                width: isMobileLandscape
                  ? '130px'
                  : screenSize.width < 480
                  ? '180px'
                  : screenSize.width < 768
                  ? '200px'
                  : isTabletLandscape
                  ? '180px'
                  : screenSize.width < 1024
                  ? '200px'
                  : '300px',
                right: isMobileLandscape
                  ? '22%'
                  : screenSize.width < 400
                  ? 'calc(50% - 90px)'
                  : screenSize.width < 500
                  ? 'calc(50% - 100px)'
                  : screenSize.width < 768
                  ? 'calc(50% - 110px)'
                  : isTabletLandscape
                  ? '8%'
                  : screenSize.width < 1024
                  ? '80px'
                  : '-100px',
                bottom: isMobileLandscape
                  ? '10px'
                  : isPortraitMobile
                  ? screenSize.height < 600
                    ? '-40px'
                    : screenSize.height < 700
                    ? '-60px'
                    : screenSize.height < 800
                    ? '-80px'
                    : '-100px'
                  : screenSize.height < 480
                  ? '-60px'
                  : screenSize.height < 768
                  ? '-80px'
                  : screenSize.height < 1024
                  ? '-100px'
                  : '40px',
              }}
            />
          </motion.div>
        </div>

        {/* CTA (countdown) - positioned relative to hero section, not foreground div */}
        <motion.a
          href="https://shutthebox.io/alpha_a/index.html"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          style={{
            position: 'absolute',
            bottom: isMobileLandscape
              ? '25%'
              : isPortraitMobile
              ? '12%'
              : screenSize.height < 768
              ? '15%'
              : screenSize.height < 1024
              ? '12%'
              : '10%',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '0',
            width: (isMobileLandscape || isPortraitMobile) ? '95%' : 'auto',
            maxWidth: isMobileLandscape ? '400px' : isPortraitMobile ? '95%' : '700px',
            zIndex: 20
          }}
        >
          <CountdownTimer />
        </motion.a>

        <div id="hero-sentinel" className="absolute bottom-0 h-[1px] w-full"></div>
      </Element>

      {/* ✅ Sentinel marker directly after hero */}
      <div className="overflow-x-hidden">
        <Element name="about" id="about">
          <AboutSection />
        </Element>
      </div>

      <Element name="tokenomics" id="tokenomics">
        <TokenomicsSection />
      </Element>
      <Element name="trust" id="trust">
        <TrustSection />
      </Element>
      <Element name="leaderboard" id="leaderboard">
        <LeaderboardSection />
      </Element>
      <Element name="roadmap" id="roadmap">
        <RoadmapSection />
      </Element>
      <Footer /> {/* ✅ This line goes here */}

      {/* Cookie Consent Banner */}
      <CookieConsent onOpenTerms={() => setTermsModalOpen(true)} />

      {/* Terms Modal (for cookie consent link) */}
      <TermsModal open={termsModalOpen} onClose={() => setTermsModalOpen(false)} />
    </div>
  );
}

export default App;
